import { useState, useEffect, useCallback } from 'react';
import { useOutletContext } from 'react-router-dom';
import { 
  Book, 
  Plus, 
  Pencil, 
  Trash2, 
  GripVertical,
  ChevronDown,
  ChevronRight,
  Save,
  X,
  Eye,
  EyeOff,
  FileText,
  Loader2,
  Search,
  RefreshCw,
  Sparkles,
  PlayCircle,
  Calendar,
  Building2,
  Truck,
  Users,
  Settings,
  Smartphone,
  Database,
  ClipboardList,
  HelpCircle
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import { Label } from '../../components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Switch } from '../../components/ui/switch';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { toast } from 'sonner';
import { useSuperAdmin } from '../../context/SuperAdminContext';

// Icon mapping
const ICON_OPTIONS = {
  PlayCircle: PlayCircle,
  ClipboardList: ClipboardList,
  Calendar: Calendar,
  Building2: Building2,
  Truck: Truck,
  Users: Users,
  FileText: FileText,
  Settings: Settings,
  Smartphone: Smartphone,
  Database: Database,
  Book: Book,
  HelpCircle: HelpCircle,
};

const HelpManagement = () => {
  const { isDark = true } = useOutletContext() || {};
  const { api } = useSuperAdmin();
  const [loading, setLoading] = useState(true);
  const [categories, setCategories] = useState([]);
  const [articles, setArticles] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  
  // Dialog states
  const [categoryDialogOpen, setCategoryDialogOpen] = useState(false);
  const [articleDialogOpen, setArticleDialogOpen] = useState(false);
  const [editingCategory, setEditingCategory] = useState(null);
  const [editingArticle, setEditingArticle] = useState(null);
  const [saving, setSaving] = useState(false);
  const [seeding, setSeeding] = useState(false);

  // Form states
  const [categoryForm, setCategoryForm] = useState({
    key: '',
    title: '',
    description: '',
    icon: 'Book',
    order: 0,
    roles: ['admin', 'dispatcher', 'operator']
  });

  const [articleForm, setArticleForm] = useState({
    category_key: '',
    title: '',
    content: '',
    badge: '',
    order: 0,
    is_published: true,
    roles: ['admin', 'dispatcher', 'operator']
  });

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const [catRes, artRes] = await Promise.all([
        api.get('/api/super-admin/help/categories'),
        api.get('/api/super-admin/help/articles')
      ]);
      setCategories(catRes.data || []);
      setArticles(artRes.data || []);
    } catch (error) {
      console.error('Failed to fetch help data:', error);
      toast.error('Failed to load help content');
    } finally {
      setLoading(false);
    }
  }, [api]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Filter articles by selected category and search
  const filteredArticles = articles.filter(article => {
    const matchesCategory = !selectedCategory || article.category_key === selectedCategory;
    const matchesSearch = !searchQuery || 
      article.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
      article.content.toLowerCase().includes(searchQuery.toLowerCase());
    return matchesCategory && matchesSearch;
  });

  // Seed initial content
  const handleSeedContent = async () => {
    if (!confirm('This will add initial help content to the database. Continue?')) return;
    
    setSeeding(true);
    try {
      const res = await api.post('/api/super-admin/help/seed');
      if (res.data.seeded) {
        toast.success(`Seeded ${res.data.categories} categories and ${res.data.articles} articles`);
        fetchData();
      } else {
        toast.info('Help content already exists');
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to seed content');
    } finally {
      setSeeding(false);
    }
  };

  // Category CRUD
  const openCategoryDialog = (category = null) => {
    if (category) {
      setEditingCategory(category);
      setCategoryForm({
        key: category.key,
        title: category.title,
        description: category.description,
        icon: category.icon || 'Book',
        order: category.order || 0,
        roles: category.roles || ['admin', 'dispatcher', 'operator']
      });
    } else {
      setEditingCategory(null);
      setCategoryForm({
        key: '',
        title: '',
        description: '',
        icon: 'Book',
        order: categories.length,
        roles: ['admin', 'dispatcher', 'operator']
      });
    }
    setCategoryDialogOpen(true);
  };

  const handleSaveCategory = async () => {
    if (!categoryForm.key || !categoryForm.title) {
      toast.error('Key and title are required');
      return;
    }

    setSaving(true);
    try {
      if (editingCategory) {
        await api.put(`/api/super-admin/help/categories/${editingCategory.id}`, categoryForm);
        toast.success('Category updated');
      } else {
        await api.post('/api/super-admin/help/categories', categoryForm);
        toast.success('Category created');
      }
      setCategoryDialogOpen(false);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save category');
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteCategory = async (category) => {
    if (!confirm(`Delete "${category.title}" and all its articles? This cannot be undone.`)) return;
    
    try {
      await api.delete(`/api/super-admin/help/categories/${category.id}`);
      toast.success('Category deleted');
      fetchData();
    } catch (error) {
      toast.error('Failed to delete category');
    }
  };

  // Article CRUD
  const openArticleDialog = (article = null) => {
    if (article) {
      setEditingArticle(article);
      setArticleForm({
        category_key: article.category_key,
        title: article.title,
        content: article.content,
        badge: article.badge || '',
        order: article.order || 0,
        is_published: article.is_published !== false,
        roles: article.roles || ['admin', 'dispatcher', 'operator']
      });
    } else {
      setEditingArticle(null);
      setArticleForm({
        category_key: selectedCategory || (categories[0]?.key || ''),
        title: '',
        content: '',
        badge: '',
        order: filteredArticles.length,
        is_published: true,
        roles: ['admin', 'dispatcher', 'operator']
      });
    }
    setArticleDialogOpen(true);
  };

  const handleSaveArticle = async () => {
    if (!articleForm.category_key || !articleForm.title || !articleForm.content) {
      toast.error('Category, title, and content are required');
      return;
    }

    setSaving(true);
    try {
      if (editingArticle) {
        await api.put(`/api/super-admin/help/articles/${editingArticle.id}`, articleForm);
        toast.success('Article updated');
      } else {
        await api.post('/api/super-admin/help/articles', articleForm);
        toast.success('Article created');
      }
      setArticleDialogOpen(false);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save article');
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteArticle = async (article) => {
    if (!confirm(`Delete "${article.title}"? This cannot be undone.`)) return;
    
    try {
      await api.delete(`/api/super-admin/help/articles/${article.id}`);
      toast.success('Article deleted');
      fetchData();
    } catch (error) {
      toast.error('Failed to delete article');
    }
  };

  const toggleArticlePublished = async (article) => {
    try {
      await api.put(`/api/super-admin/help/articles/${article.id}`, { is_published: !article.is_published });
      toast.success(article.is_published ? 'Article unpublished' : 'Article published');
      fetchData();
    } catch (error) {
      toast.error('Failed to update article');
    }
  };

  const toggleRole = (role) => {
    const roles = categoryForm.roles || [];
    if (roles.includes(role)) {
      setCategoryForm({ ...categoryForm, roles: roles.filter(r => r !== role) });
    } else {
      setCategoryForm({ ...categoryForm, roles: [...roles, role] });
    }
  };

  const toggleArticleRole = (role) => {
    const roles = articleForm.roles || [];
    if (roles.includes(role)) {
      setArticleForm({ ...articleForm, roles: roles.filter(r => r !== role) });
    } else {
      setArticleForm({ ...articleForm, roles: [...roles, role] });
    }
  };

  const getCategoryIcon = (iconName) => {
    const IconComponent = ICON_OPTIONS[iconName] || Book;
    return <IconComponent className="h-4 w-4" />;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2 text-gray-900 dark:text-white">
            <Book className="h-6 w-6 text-[#ffd108]" />
            Help Center Management
          </h1>
          <p className="text-gray-500 dark:text-slate-400">
            Manage documentation and help articles for users
          </p>
        </div>
        <div className="flex items-center gap-2">
          {categories.length === 0 && (
            <Button 
              onClick={handleSeedContent} 
              disabled={seeding}
              variant="outline"
              className="gap-2"
            >
              {seeding ? <Loader2 className="h-4 w-4 animate-spin" /> : <Sparkles className="h-4 w-4" />}
              Seed Initial Content
            </Button>
          )}
          <Button onClick={fetchData} variant="outline" size="icon" className="border-gray-200 dark:border-slate-700 text-gray-600 dark:text-white hover:bg-gray-100 dark:hover:bg-slate-800">
            <RefreshCw className="h-4 w-4" />
          </Button>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card className="bg-white dark:bg-slate-900 border-gray-200 dark:border-slate-800">
          <CardContent className="pt-6">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 rounded-full bg-blue-100 dark:bg-blue-900/30 flex items-center justify-center">
                <Book className="h-6 w-6 text-blue-600" />
              </div>
              <div>
                <p className="text-2xl font-bold">{categories.length}</p>
                <p className="text-sm text-muted-foreground">Categories</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="bg-white dark:bg-slate-900 border-gray-200 dark:border-slate-800">
          <CardContent className="pt-6">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 rounded-full bg-green-100 dark:bg-green-900/30 flex items-center justify-center">
                <FileText className="h-6 w-6 text-green-600" />
              </div>
              <div>
                <p className="text-2xl font-bold">{articles.length}</p>
                <p className="text-sm text-muted-foreground">Articles</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="bg-white dark:bg-slate-900 border-gray-200 dark:border-slate-800">
          <CardContent className="pt-6">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 rounded-full bg-yellow-100 dark:bg-yellow-900/30 flex items-center justify-center">
                <Eye className="h-6 w-6 text-yellow-600" />
              </div>
              <div>
                <p className="text-2xl font-bold">{articles.filter(a => a.is_published).length}</p>
                <p className="text-sm text-muted-foreground">Published</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      <Tabs defaultValue="categories" className="space-y-4">
        <TabsList className="bg-gray-100 dark:bg-slate-900 border border-gray-200 dark:border-slate-800">
          <TabsTrigger value="categories" className="gap-2 data-[state=active]:bg-white dark:data-[state=active]:bg-slate-800 data-[state=active]:text-gray-900 dark:data-[state=active]:text-white">
            <Book className="h-4 w-4" />
            Categories
          </TabsTrigger>
          <TabsTrigger value="articles" className="gap-2 data-[state=active]:bg-white dark:data-[state=active]:bg-slate-800 data-[state=active]:text-gray-900 dark:data-[state=active]:text-white">
            <FileText className="h-4 w-4" />
            Articles
          </TabsTrigger>
        </TabsList>

        {/* Categories Tab */}
        <TabsContent value="categories" className="space-y-4">
          <div className="flex items-center justify-between">
            <p className="text-sm text-muted-foreground">
              Organize help content into categories. Users see categories based on their role.
            </p>
            <Button onClick={() => openCategoryDialog()} className="gap-2">
              <Plus className="h-4 w-4" />
              Add Category
            </Button>
          </div>

          <div className="grid gap-4">
            {categories.length === 0 ? (
              <Card className="bg-white dark:bg-slate-900 border-gray-200 dark:border-slate-800">
                <CardContent className="py-12 text-center">
                  <Book className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
                  <h3 className="font-semibold text-lg mb-2">No categories yet</h3>
                  <p className="text-muted-foreground mb-4">
                    Create categories to organize your help content, or seed initial content.
                  </p>
                  <Button onClick={() => openCategoryDialog()}>
                    <Plus className="h-4 w-4 mr-2" />
                    Create First Category
                  </Button>
                </CardContent>
              </Card>
            ) : (
              categories.map((category, idx) => (
                <Card key={category.id} className="bg-white dark:bg-slate-900 border-gray-200 dark:border-slate-800 hover:border-[#ffd108]/50 transition-colors">
                  <CardContent className="py-4">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-4">
                        <div className="flex items-center gap-2 text-muted-foreground">
                          <GripVertical className="h-4 w-4" />
                          <span className="text-sm font-mono">#{idx + 1}</span>
                        </div>
                        <div className="w-10 h-10 rounded-lg bg-[#ffd108]/20 flex items-center justify-center">
                          {getCategoryIcon(category.icon)}
                        </div>
                        <div>
                          <div className="flex items-center gap-2">
                            <h3 className="font-semibold">{category.title}</h3>
                            <Badge variant="outline" className="font-mono text-xs">
                              {category.key}
                            </Badge>
                          </div>
                          <p className="text-sm text-muted-foreground">{category.description}</p>
                          <div className="flex items-center gap-2 mt-1">
                            {category.roles?.map(role => (
                              <Badge key={role} variant="secondary" className="text-xs">
                                {role}
                              </Badge>
                            ))}
                            <span className="text-xs text-muted-foreground ml-2">
                              {articles.filter(a => a.category_key === category.key).length} articles
                            </span>
                          </div>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => openCategoryDialog(category)}
                        >
                          <Pencil className="h-4 w-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleDeleteCategory(category)}
                          className="text-red-500 hover:text-red-600"
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))
            )}
          </div>
        </TabsContent>

        {/* Articles Tab */}
        <TabsContent value="articles" className="space-y-4">
          <div className="flex items-center justify-between gap-4">
            <div className="flex items-center gap-4 flex-1">
              <div className="relative flex-1 max-w-sm">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400 dark:text-slate-500" />
                <Input
                  placeholder="Search articles..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-10 bg-white dark:bg-slate-900 border-gray-200 dark:border-slate-700 text-gray-900 dark:text-white placeholder:text-gray-400 dark:placeholder:text-slate-500"
                />
              </div>
              <Select value={selectedCategory || 'all'} onValueChange={(v) => setSelectedCategory(v === 'all' ? null : v)}>
                <SelectTrigger className="w-[200px] bg-white dark:bg-slate-900 border-gray-200 dark:border-slate-700 text-gray-900 dark:text-white">
                  <SelectValue placeholder="All categories" />
                </SelectTrigger>
                <SelectContent className="bg-white dark:bg-slate-900 border-gray-200 dark:border-slate-700">
                  <SelectItem value="all">All Categories</SelectItem>
                  {categories.map(cat => (
                    <SelectItem key={cat.key} value={cat.key}>{cat.title}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <Button onClick={() => openArticleDialog()} className="gap-2" disabled={categories.length === 0}>
              <Plus className="h-4 w-4" />
              Add Article
            </Button>
          </div>

          <div className="grid gap-4">
            {filteredArticles.length === 0 ? (
              <Card className="bg-white dark:bg-slate-900 border-gray-200 dark:border-slate-800">
                <CardContent className="py-12 text-center">
                  <FileText className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
                  <h3 className="font-semibold text-lg mb-2">No articles found</h3>
                  <p className="text-muted-foreground mb-4">
                    {categories.length === 0 
                      ? 'Create a category first, then add articles.'
                      : 'Create your first article to help users.'}
                  </p>
                  {categories.length > 0 && (
                    <Button onClick={() => openArticleDialog()}>
                      <Plus className="h-4 w-4 mr-2" />
                      Create Article
                    </Button>
                  )}
                </CardContent>
              </Card>
            ) : (
              filteredArticles.map((article, idx) => {
                const category = categories.find(c => c.key === article.category_key);
                const articleRoles = article.roles || ['admin', 'dispatcher', 'operator'];
                return (
                  <Card key={article.id} className={`bg-white dark:bg-slate-900 border-gray-200 dark:border-slate-800 hover:border-[#ffd108]/50 transition-colors ${!article.is_published ? 'opacity-60' : ''}`}>
                    <CardContent className="py-4">
                      <div className="flex items-start justify-between">
                        <div className="flex items-start gap-4 flex-1">
                          <div className="flex flex-col items-center gap-1 text-muted-foreground">
                            <GripVertical className="h-4 w-4" />
                            <span className="text-xs font-mono">#{article.order + 1}</span>
                          </div>
                          <div className="flex-1">
                            <div className="flex items-center gap-2 mb-1">
                              <h3 className="font-semibold">{article.title}</h3>
                              {article.badge && (
                                <Badge className="bg-[#ffd108]/20 text-[#ffd108]">
                                  {article.badge}
                                </Badge>
                              )}
                              {!article.is_published && (
                                <Badge variant="outline" className="text-orange-500 border-orange-500">
                                  Draft
                                </Badge>
                              )}
                            </div>
                            <div className="flex items-center gap-2 text-sm text-muted-foreground mb-2">
                              {category && (
                                <>
                                  {getCategoryIcon(category.icon)}
                                  <span>{category.title}</span>
                                </>
                              )}
                              <span className="text-muted-foreground/50">•</span>
                              <span className="text-xs">
                                Viewable by: {articleRoles.map(r => r.charAt(0).toUpperCase() + r.slice(1)).join(', ')}
                              </span>
                            </div>
                            <p className="text-sm text-muted-foreground line-clamp-2">
                              {article.content.substring(0, 200)}...
                            </p>
                          </div>
                        </div>
                        <div className="flex items-center gap-1">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => toggleArticlePublished(article)}
                            title={article.is_published ? 'Unpublish' : 'Publish'}
                          >
                            {article.is_published ? (
                              <Eye className="h-4 w-4" />
                            ) : (
                              <EyeOff className="h-4 w-4" />
                            )}
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => openArticleDialog(article)}
                          >
                            <Pencil className="h-4 w-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleDeleteArticle(article)}
                            className="text-red-500 hover:text-red-600"
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                );
              })
            )}
          </div>
        </TabsContent>
      </Tabs>

      {/* Category Dialog */}
      <Dialog open={categoryDialogOpen} onOpenChange={setCategoryDialogOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>
              {editingCategory ? 'Edit Category' : 'Add Category'}
            </DialogTitle>
            <DialogDescription>
              Categories organize help articles. Users see categories based on their role.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Key (unique identifier)</Label>
                <Input
                  value={categoryForm.key}
                  onChange={(e) => setCategoryForm({ ...categoryForm, key: e.target.value.replace(/\s/g, '') })}
                  placeholder="gettingStarted"
                  disabled={!!editingCategory}
                />
              </div>
              <div className="space-y-2">
                <Label>Icon</Label>
                <Select value={categoryForm.icon} onValueChange={(v) => setCategoryForm({ ...categoryForm, icon: v })}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {Object.keys(ICON_OPTIONS).map(icon => (
                      <SelectItem key={icon} value={icon}>
                        <div className="flex items-center gap-2">
                          {getCategoryIcon(icon)}
                          {icon}
                        </div>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="space-y-2">
              <Label>Title</Label>
              <Input
                value={categoryForm.title}
                onChange={(e) => setCategoryForm({ ...categoryForm, title: e.target.value })}
                placeholder="Getting Started"
              />
            </div>
            <div className="space-y-2">
              <Label>Description</Label>
              <Input
                value={categoryForm.description}
                onChange={(e) => setCategoryForm({ ...categoryForm, description: e.target.value })}
                placeholder="Learn the basics of EQUIPR CORE"
              />
            </div>
            <div className="space-y-2">
              <Label>Visible to Roles</Label>
              <div className="flex items-center gap-4">
                {['admin', 'dispatcher', 'operator'].map(role => (
                  <label key={role} className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={categoryForm.roles?.includes(role)}
                      onChange={() => toggleRole(role)}
                      className="rounded"
                    />
                    <span className="text-sm capitalize">{role}</span>
                  </label>
                ))}
              </div>
            </div>
            <div className="space-y-2">
              <Label>Order</Label>
              <Input
                type="number"
                value={categoryForm.order}
                onChange={(e) => setCategoryForm({ ...categoryForm, order: parseInt(e.target.value) || 0 })}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCategoryDialogOpen(false)}>
              Cancel
            </Button>
            <Button onClick={handleSaveCategory} disabled={saving}>
              {saving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Save className="h-4 w-4 mr-2" />}
              Save
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Article Dialog */}
      <Dialog open={articleDialogOpen} onOpenChange={setArticleDialogOpen}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              {editingArticle ? 'Edit Article' : 'Add Article'}
            </DialogTitle>
            <DialogDescription>
              Write help content using markdown-style formatting. Use **bold**, bullet points (- item), and numbered lists.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Category</Label>
                <Select value={articleForm.category_key} onValueChange={(v) => setArticleForm({ ...articleForm, category_key: v })}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select category" />
                  </SelectTrigger>
                  <SelectContent>
                    {categories.map(cat => (
                      <SelectItem key={cat.key} value={cat.key}>
                        <div className="flex items-center gap-2">
                          {getCategoryIcon(cat.icon)}
                          {cat.title}
                        </div>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Badge (optional)</Label>
                <Input
                  value={articleForm.badge}
                  onChange={(e) => setArticleForm({ ...articleForm, badge: e.target.value })}
                  placeholder="Essential, New, Updated"
                />
              </div>
            </div>
            <div className="space-y-2">
              <Label>Title</Label>
              <Input
                value={articleForm.title}
                onChange={(e) => setArticleForm({ ...articleForm, title: e.target.value })}
                placeholder="How to Create a Job"
              />
            </div>
            <div className="space-y-2">
              <Label>Content</Label>
              <Textarea
                value={articleForm.content}
                onChange={(e) => setArticleForm({ ...articleForm, content: e.target.value })}
                placeholder="Write your help content here...

Use **bold** for emphasis
- Use bullet points for lists
1. Use numbered lists for steps"
                rows={15}
                className="font-mono text-sm"
              />
            </div>
            <div className="space-y-2">
              <Label>Viewable By Roles</Label>
              <p className="text-xs text-muted-foreground mb-2">
                Control which user roles can see this specific article
              </p>
              <div className="flex items-center gap-4">
                {['admin', 'dispatcher', 'operator'].map(role => (
                  <label key={role} className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={articleForm.roles?.includes(role)}
                      onChange={() => toggleArticleRole(role)}
                      className="rounded"
                    />
                    <span className="text-sm capitalize">{role}</span>
                  </label>
                ))}
              </div>
            </div>
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Switch
                  checked={articleForm.is_published}
                  onCheckedChange={(checked) => setArticleForm({ ...articleForm, is_published: checked })}
                />
                <Label>Published</Label>
              </div>
              <div className="flex items-center gap-2">
                <Label>Order:</Label>
                <Input
                  type="number"
                  value={articleForm.order}
                  onChange={(e) => setArticleForm({ ...articleForm, order: parseInt(e.target.value) || 0 })}
                  className="w-20"
                />
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setArticleDialogOpen(false)}>
              Cancel
            </Button>
            <Button onClick={handleSaveArticle} disabled={saving}>
              {saving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Save className="h-4 w-4 mr-2" />}
              Save Article
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default HelpManagement;
